/**
 * The decision calls for `/stablecoins/invoices/resolutions`, wired the way the
 * transfer approval page wires its own (ALL-5908).
 *
 * `withStepUpRetry` (`src/lib/api.ts`) is the one place that may complete an
 * AAL2 step-up and re-issue a write, and it decodes its request as
 * `{ ok, reasonCode? }` — the shape `approveApproval`/`rejectApproval` return.
 * The invoicing client returns `{ ok, error: ApiErrorInfo }` instead, so this
 * module is the adapter between the two, and the only place a decision's
 * refusals are read.
 *
 * What is deliberately absent, matching the transfer approval page exactly:
 *
 *   - **no resume descriptor.** `STEP_UP_RESUME_ACTIONS` is a closed union of
 *     three admin operations (`api.ts`), and it is not this task's file to
 *     extend. Without a descriptor `withStepUpRetry` never redirects the
 *     browser and never persists a replay, which is the safe default: an
 *     unresumable redirect would drop the approver's decision silently.
 *   - **no second attempt of a refused decision.** The only retry that exists
 *     here is the one `withStepUpRetry` performs *after* a step-up it completed
 *     itself, re-issued with the same idempotency key so the gateway sees one
 *     write. A refused decision is shown and stops.
 */

import {
  approveInvoiceResolution,
  cancelInvoiceResolution,
  rejectInvoiceResolution,
  type ApiResult,
} from "@/features/invoicing/api";
import type {
  DecideResolutionRequest,
  Resolution,
  ResolutionDecision,
} from "@/features/invoicing/types";
import { idempotencyKey, withStepUpRetry } from "@/lib/api";

/** A decision call's result in the shape `withStepUpRetry` decodes. */
export type DecisionOutcome =
  | { ok: true; decision: ResolutionDecision; reasonCode?: undefined }
  | { ok: false; reasonCode: string; supportReference?: string; capability?: string };

function adapt(result: ApiResult<ResolutionDecision>): DecisionOutcome {
  if (result.ok) return { ok: true, decision: result.data };
  return {
    ok: false,
    reasonCode: result.error.reasonCode,
    supportReference: result.error.supportReference,
    capability: result.error.capability,
  };
}

/**
 * The version token sent as `If-Match`. The contract's resolution payload
 * carries no numeric `version`, so the `updatedAt` the approver actually read is
 * the token: a decision made against a proposal that has moved on is refused
 * with `VERSION_CONFLICT`, and the page reloads rather than re-deciding blind.
 */
export function decisionVersion(resolution: Resolution): string {
  return resolution.updatedAt;
}

/** `POST .../approve`, under the house step-up helper. */
export function approveResolution(
  resolution: Resolution,
  body: DecideResolutionRequest = {},
) {
  const key = idempotencyKey("invoicing_resolution_approve");
  return withStepUpRetry(() =>
    approveInvoiceResolution(resolution.resolutionId, body, {
      ifMatch: decisionVersion(resolution),
      idempotencyKey: key,
    }).then(adapt),
  );
}

/** `POST .../reject`, under the house step-up helper. `reason` is required by the contract. */
export function rejectResolution(resolution: Resolution, reason: string) {
  const key = idempotencyKey("invoicing_resolution_reject");
  return withStepUpRetry(() =>
    rejectInvoiceResolution(
      resolution.resolutionId,
      { reason },
      { ifMatch: decisionVersion(resolution), idempotencyKey: key },
    ).then(adapt),
  );
}

/**
 * `POST .../cancel` — the proposer withdrawing their own proposal. Not a
 * four-eyes decision, so it is not wrapped in step-up: the contract requires the
 * propose permission and the proposer's own principal, and the screen shows the
 * service's refusal (`FORBIDDEN` for anyone else) verbatim.
 */
export async function cancelResolution(resolution: Resolution): Promise<DecisionOutcome> {
  const key = idempotencyKey("invoicing_resolution_cancel");
  const result: ApiResult<Resolution> = await cancelInvoiceResolution(resolution.resolutionId, {
    idempotencyKey: key,
  });
  if (!result.ok) {
    return {
      ok: false,
      reasonCode: result.error.reasonCode,
      supportReference: result.error.supportReference,
      capability: result.error.capability,
    };
  }
  return { ok: true, decision: { resolution: result.data, refund: null } };
}

/** A refusal the page renders: the service's code, its reference, and how it arose. */
export interface DecisionRefusal {
  reasonCode: string;
  supportReference?: string;
  capability?: string;
  /** A step-up was attempted and itself failed — a distinct message, not the raw code. */
  stepUpFailed: boolean;
  /** The step-up's own code, when it failed for a reason of its own. */
  stepUpReasonCode?: string;
}

/** Collapses a `withStepUpRetry` outcome into the refusal the page shows, or null on success. */
export function refusalFromOutcome(
  outcome: Awaited<ReturnType<typeof approveResolution>>,
): DecisionRefusal | null {
  if (outcome.result.ok) return null;
  return {
    reasonCode: outcome.result.reasonCode,
    supportReference: outcome.result.supportReference ?? outcome.stepUpSupportReference,
    capability: outcome.result.capability,
    stepUpFailed: outcome.stepUpFailed,
    stepUpReasonCode: outcome.stepUpReasonCode,
  };
}
